import { DatePicker, Form, Input, InputNumber, message, Select } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPicturesFromFormDataForUpload, nullSafeGet } from '../../utils/DataAccessUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { contractorLocationsRestCrudThunksForSupplier } from '../../thunks/contractor_locations_thunks';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { contractorClientsRestCrudThunksForSupplier } from '../../thunks/contractor_clients_thunks';
import BackButton from '../back_button/BackButton';
import { workOrdersRestCrudThunksForSupplier } from '../../thunks/work_orders_thunks';
import { contractorProblemTypesRestCrudThunksForSupplier } from '../../thunks/contractor_problem_types_thunks';
import { contractorWOPrioritiesRestCrudThunksForSupplier } from '../../thunks/contractor_wo_priorities_thunks';
import { CONTRACTOR_PROPS, ROLE_TYPES } from '../../utils/DataConstants';
import OWUpload from '../OWUpload';
import { imageUploadValidation, imageWildcardUploadAccept } from '../../utils/ImageUtils';
import { Icon as LegacyIcon } from '@ant-design/compatible';
import { getDefaultFileListFromString } from '../../utils/FileAccessUtils';
import OWRadioGroup from '../ow_radio_group/OWRadioGroup';
import OWRichTextEditor from '../../rich_text_editor/OWRichTextEditor';
import { WO_DATE_FORMAT } from '../../utils/DataFormatterUtils';
import moment from 'moment';
import OWButton from '../ow_button/OWButton';

const CONTRACTOR_WO_EDIT_AUTOCOMPLETE = 'CONTRACTOR_WO_EDIT_AUTOCOMPLETE';

const ContractorWorkOrderEditForm: FC<any> = ({
	formData,
	contractorLocations,
	fetchContractorLocations,
	fetchContractorClients,
	contractorClients,
	onSuccess,
	updateWorkOrder,
	contractorProblemTypes,
	fetchContractorProblemTypes,
	contractorWOPriorities,
	fetchContractorWOPriorities,
}): React.ReactElement => {
	const additionalInfoRef = useRef(null);

	const [updating, setUpdating] = useState(false);
	const [uploading, setUploading] = useState(false);
	// Read straight off `formData` so the reference stays stable across renders and the effect below
	// only fires when the work order actually changes.
	const formDataImages = nullSafeGet('images', formData);
	const convertedImages = nullSafeGet('convertedImages', formData);

	const [images, setImages] = useState(
		getDefaultFileListFromString(formDataImages, ROLE_TYPES.SUPPLIER, convertedImages)
	);

	// Conversions land seconds after the attachment does, so the initial state alone would keep
	// showing the undecodable original. `images` is also what handleSubmit sends, so only re-derive
	// while it still matches the saved list — photos the user added or removed are left alone.
	useEffect(() => {
		setImages((currentImages) => {
			const savedImages = formDataImages || [];
			// A length mismatch also catches an upload still in flight, which carries no response yet.
			const isPristine =
				currentImages.length === savedImages.length &&
				getPicturesFromFormDataForUpload(currentImages).join(',') === savedImages.join(',');
			return isPristine
				? getDefaultFileListFromString(formDataImages, ROLE_TYPES.SUPPLIER, convertedImages)
				: currentImages;
		});
	}, [formDataImages, convertedImages]);

	const initialValue = useMemo(
		() => ({
			...formData,
			images: {
				fileList: getDefaultFileListFromString(
					formDataImages,
					ROLE_TYPES.SUPPLIER,
					convertedImages
				),
			},
			contractorLocationId: nullSafeGet('subContractedToWorkOrder.contractorLocationId', formData),
			contractorClientId: nullSafeGet('subContractedToWorkOrder.contractorClientId', formData),
			contractorProblemTypeId: nullSafeGet(
				'subContractedToWorkOrder.contractorProblemTypeId',
				formData
			),
			contractorWorkOrderPriorityId: nullSafeGet(
				'subContractedToWorkOrder.contractorWorkOrderPriorityId',
				formData
			),
		}),
		[formData, formDataImages, convertedImages]
	);

	const getAdditionalInfo = useCallback(
		() => nullSafeGet('current', additionalInfoRef) && additionalInfoRef.current.getContent(),
		[]
	);

	const handleSubmit = useCallback(
		(values) => {
			setUpdating(true);
			updateWorkOrder({
				...formData,
				...values,
				description: getAdditionalInfo(),
				images: getPicturesFromFormDataForUpload(images),
				contractorWoPriorityId: values.contractorWorkOrderPriorityId,
				contractorWorkOrderId: nullSafeGet(`${CONTRACTOR_PROPS.TO_WO}.id`, formData),
			})
				.then(onSuccess)
				.catch(message.error)
				.finally(() => setUpdating(false));
		},
		[formData, getAdditionalInfo, images, onSuccess, updateWorkOrder]
	);

	const onImageUploadError = useCallback(() => {
		message.error('Image upload failed! Please contact support!');
		setUploading(false);
	}, []);

	const handleImageUpdate = useCallback((info) => {
		setUploading(info.file.status === 'uploading');
		if (info.file.status !== undefined) {
			setImages(info.fileList);
		}
	}, []);

	return (
		<Form
			onFinish={handleSubmit}
			layout="vertical"
			initialValues={{
				...(initialValue || {}),
				...(formData.dueDate && { dueDate: moment(formData.dueDate) }),
			}}
			style={{ maxWidth: 800 }}
		>
			<Form.Item
				label="Title"
				name="title"
				rules={[{ required: true, message: 'This field is required.' }]}
			>
				<Input />
			</Form.Item>
			<Form.Item label="Alias Work Order Number" name="woNumber">
				<Input />
			</Form.Item>
			<Form.Item
				label="Client"
				name="contractorClientId"
				rules={[{ required: true, message: 'This field is required.' }]}
			>
				<OWAsyncSelect
					disabled
					stateSlice={contractorClients}
					targetCollectionName={CONTRACTOR_WO_EDIT_AUTOCOMPLETE}
					fetchMultiple={(ids, targetCollectionName) => {}}
					fetchData={(
						searchText,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					) => {
						fetchContractorClients(
							{ search: searchText || undefined },
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						);
					}}
					renderRecord={(client) => (
						<Select.Option key={client.id} value={client.id}>
							{client.name}
						</Select.Option>
					)}
					sortBy={{ sort_by: 'name', order: 'ascend' }}
				/>
			</Form.Item>
			<Form.Item
				label="Incident Location"
				name="contractorLocationId"
				rules={[{ required: true, message: 'This field is required.' }]}
			>
				<OWAsyncSelect
					stateSlice={contractorLocations}
					targetCollectionName={CONTRACTOR_WO_EDIT_AUTOCOMPLETE}
					fetchMultiple={(ids, targetCollectionName) => {}}
					fetchData={(
						searchText,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					) => {
						fetchContractorLocations(
							{ search: searchText || undefined },
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						);
					}}
					additionalFilters={{
						contractorClientId: nullSafeGet('contractorClientId', initialValue),
					}}
					renderRecord={(location) => (
						<Select.Option key={location.id} value={location.id}>
							<div>{location.name}</div>
							<div style={{ color: 'rgba(0,0,0,0.45)' }}>
								{nullSafeGet('primaryAddress.streetAddress1', location)},{' '}
								{nullSafeGet('primaryAddress.city', location)}{' '}
								{nullSafeGet('primaryAddress.region', location)}
							</div>
						</Select.Option>
					)}
					sortBy={{ sort_by: 'name', order: 'ascend' }}
				/>
			</Form.Item>
			<Form.Item
				label="Problem Type"
				name="contractorProblemTypeId"
				rules={[{ required: true, message: 'This field is required.' }]}
			>
				<OWAsyncSelect
					stateSlice={contractorProblemTypes}
					targetCollectionName={CONTRACTOR_WO_EDIT_AUTOCOMPLETE}
					fetchMultiple={(ids, targetCollectionName) => {}}
					fetchData={(
						searchText,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					) =>
						fetchContractorProblemTypes(
							{ name: searchText || undefined },
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						)
					}
					additionalFilters={{
						contractorClientId: nullSafeGet('contractorClientId', initialValue),
					}}
					renderRecord={(pt) => (
						<Select.Option key={pt.id} value={pt.id}>
							{pt.hierarchyName || pt.name}
						</Select.Option>
					)}
					sortBy={{ sort_by: 'name', order: 'ascend' }}
				/>
			</Form.Item>
			<Form.Item label="NTE" name="nte">
				<InputNumber addonAfter={`USD`} />
			</Form.Item>

			<Form.Item label="Under Warranty ?" name="isWarranty">
				<OWRadioGroup
					valueAccessor={(item) => item.value}
					renderRecord={(item) => item.label}
					items={[
						{
							label: 'Yes',
							value: true,
						},
						{
							label: 'No',
							value: false,
						},
					]}
				/>
			</Form.Item>
			<Form.Item label="Additional Info" name="additionalInfo">
				<OWRichTextEditor initialEditorState={formData.description} editorRef={additionalInfoRef} />
			</Form.Item>
			<Form.Item name="images" label="Photos">
				<OWUpload
					accept={imageWildcardUploadAccept}
					name="file"
					listType="picture-card"
					className="workorder-image-uploader"
					multiple={true}
					fileList={images}
					beforeUpload={imageUploadValidation}
					onError={onImageUploadError}
					onChange={handleImageUpdate}
				>
					<div style={{ display: 'inline' }}>
						<LegacyIcon type={uploading ? 'loading' : 'plus'} />
						<div className="ant-upload-text">Upload</div>
					</div>
				</OWUpload>
			</Form.Item>
			<Form.Item
				label="Priority"
				name="contractorWorkOrderPriorityId"
				rules={[{ required: true, message: 'This field is required.' }]}
			>
				<OWAsyncSelect
					stateSlice={contractorWOPriorities}
					targetCollectionName={CONTRACTOR_WO_EDIT_AUTOCOMPLETE}
					fetchMultiple={(ids, targetCollectionName) => {}}
					fetchData={(
						searchText,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					) =>
						fetchContractorWOPriorities(
							{ name: searchText || undefined },
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						)
					}
					additionalFilters={{
						contractorClientId: nullSafeGet('contractorClientId', initialValue),
					}}
					renderRecord={(pt) => (
						<Select.Option key={pt.id} value={pt.id}>
							{pt.hierarchyName || pt.name}
						</Select.Option>
					)}
					sortBy={{ sort_by: 'name', order: 'ascend' }}
				/>
			</Form.Item>
			<Form.Item label="Due date" name="dueDate">
				<DatePicker format={WO_DATE_FORMAT} showTime={true} />
			</Form.Item>
			<Form.Item>
				<OWButton
					type="primary"
					htmlType="submit"
					loading={updating || uploading}
					className="contactForm__button"
				>
					Update work order
				</OWButton>
				<span style={{ marginLeft: '16px' }}>
					<BackButton buttonText="Cancel" />
				</span>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state) => ({
	contractorLocations: state.contractor_locations,
	contractorClients: state.contractor_clients,
	contractorProblemTypes: state.contractor_problem_types,
	contractorWOPriorities: state.contractor_wo_priorities,
});

const mapDispatchToProps = (dispatch) => ({
	updateWorkOrder: (entity) => dispatch(workOrdersRestCrudThunksForSupplier.update(entity)),
	fetchMultipleContractorLocations: (ids, targetCollectionName) =>
		dispatch(contractorLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchContractorLocation: (id) =>
		dispatch(contractorLocationsRestCrudThunksForSupplier.readOne(id)),
	fetchContractorLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			contractorLocationsRestCrudThunksForSupplier.read(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchContractorClients: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			contractorClientsRestCrudThunksForSupplier.read(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchContractorProblemTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			contractorProblemTypesRestCrudThunksForSupplier.read(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchContractorWOPriorities: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			contractorWOPrioritiesRestCrudThunksForSupplier.read(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(ContractorWorkOrderEditForm)
);
